import { ArrowLeftIcon } from '@phosphor-icons/react';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAnonymous, useAuthStore } from '~/store/authStore.js';
import { ButtonLink } from '~/ui/Button.js';
import { cx } from '~/ui/cx.js';
import { MenuTile, type MenuTileSuit } from '~/ui/MenuTile.js';
import { QuietGround } from '~/ui/QuietGround.js';
import { Screen } from '~/ui/Screen.js';
import { SuitLine } from '~/ui/SuitLine.js';
import { type AccountFlash, AccountPage } from './settings/AccountPage.js';
import {
  ForgotPasswordPage,
  NeedAccountPage,
  NewPasswordPage,
  RegisterPage,
  SignInPage,
} from './settings/AccountEntry.js';
import { AvatarPage } from './settings/AvatarPage.js';
import { DeleteAccountPage } from './settings/DeleteAccountPage.js';
import { ChangePlanPage, OfferPage, PlansPage, SubscribedPage } from './settings/OfferPages.js';
import { GameSettings, LookSoundSettings } from './settings/PreferencePages.js';
import { RestorePage } from './settings/RestorePage.js';
import { SubscriptionPage } from './settings/SubscriptionPage.js';
import { SupportPage } from './settings/SupportPage.js';
import {
  ACCOUNT_PATH,
  type AccountSubPage,
  PROFILE_PATH,
  isAccountSubPage,
  openedFromProfile,
  resumeOf,
} from './settings/accountPages.js';
import {
  FOCUS,
  SUBSCRIPTION_PATH,
  type SubscriptionSubPage,
  isMember,
  isSubscriptionSubPage,
  onward,
  standingOf,
  subscriptionPath,
  subscriptionState,
  wayOut,
} from './settings/subscriptionPages.js';

/**
 * Settings from the menu: groups of preferences, each on its own page.
 *
 * Drawn to the settings sketches (`lobby-settings`, `lobby-account-settings`)
 * and `docs/ux/account/settings.md`. A phone opens the group list first, then
 * the chosen group, and Back from a group returns to the list. From the tablet
 * breakpoint the groups stand in a sidebar beside the open one, `/settings`
 * opens Game, and Back goes to the Arcade. Both are one page in the markup,
 * with the breakpoint deciding which half shows, so a URL means the same thing
 * at every width.
 *
 * The Account group's own pages (create an account, sign in, a forgotten
 * password, the guest wall, Change avatar, delete) stand alone in a narrow column at every
 * width, with Back to
 * Account: they are one question each, and a sidebar beside a form reads as
 * a way out of it.
 *
 * Subscription, the fourth group, holds where the player stands and the way
 * into the offer. Its own pages (the offer, the plans, Subscribed, restore,
 * Change plan) stand alone like the Account group's, with Back to wherever
 * they were opened from (`settings/subscriptionPages.ts`). The table's drawer
 * still draws `SettingsList`.
 *
 * Help and support is a ghost chip under the doors rather than a door: it
 * holds no settings. `/settings/support` opens it in the group's place, with
 * no door shaded.
 *
 * Focus follows the spec: the heading on entry, and the door or chip that
 * opened a page when coming back from it. A link that leads back says which
 * one in its state as `returnTo`, and the control wears the same key as
 * `data-focus-key`.
 */

type Group = 'game' | 'look-sound' | 'account' | 'subscription';

interface GroupDoor {
  readonly slug: Group;
  readonly key: 'game' | 'lookSound' | 'account' | 'subscription';
  readonly suit: MenuTileSuit;
}

/** The group `/settings` opens on a wide screen. */
const GAME: GroupDoor = { slug: 'game', key: 'game', suit: 'spade' };

/** The support page's slug, and the focus key its chip wears. */
const SUPPORT = 'support';

const GROUPS: readonly GroupDoor[] = [
  GAME,
  { slug: 'look-sound', key: 'lookSound', suit: 'heart' },
  { slug: 'account', key: 'account', suit: 'diamond' },
  { slug: 'subscription', key: 'subscription', suit: 'club' },
];

const SUBPAGE_TITLE = {
  register: 'settings.account.registerTitle',
  'sign-in': 'settings.account.signInTitle',
  'forgot-password': 'settings.account.forgotTitle',
  'new-password': 'settings.account.newPasswordTitle',
  'need-name': 'settings.account.needTitle',
  'need-avatar': 'settings.account.needTitle',
  avatar: 'settings.account.avatarTitle',
  delete: 'settings.account.deleteTitle',
} as const satisfies Record<AccountSubPage, string>;

const SUBSCRIPTION_TITLE = {
  offer: 'subscription.offer.title',
  plans: 'subscription.plans.title',
  subscribed: 'subscription.subscribed.title',
  restore: 'subscription.restore.title',
  'change-plan': 'subscription.changePlan.title',
} as const satisfies Record<SubscriptionSubPage, string>;

interface ReturnState {
  readonly returnTo?: string;
  readonly flash?: AccountFlash;
}

export function SettingsScreen(): React.JSX.Element {
  const { group, page } = useParams();
  const location = useLocation();
  const state = (location.state ?? {}) as ReturnState;
  const frame = useRef<HTMLDivElement>(null);

  /*
   * The heading on every arrival, or the control this page was left from. A
   * page draws a heading per breakpoint where the two differ, so the one to
   * focus is the one the reader can see.
   */
  useLayoutEffect(() => {
    const root = frame.current;
    if (root === null) return;
    const visible = (element: HTMLElement): boolean => element.offsetParent !== null;
    const back =
      state.returnTo === undefined
        ? []
        : [...root.querySelectorAll<HTMLElement>(`[data-focus-key="${state.returnTo}"]`)];
    // A key on a wrapper (the Manage menu's) means the control inside it.
    const control = (element: HTMLElement | undefined): HTMLElement | undefined =>
      element === undefined || element.matches('a, button')
        ? element
        : (element.querySelector<HTMLElement>('a, button') ?? undefined);
    const headings = [...root.querySelectorAll<HTMLElement>('h1')];
    (control(back.find(visible)) ?? headings.find(visible) ?? headings[0])?.focus({
      preventScroll: true,
    });
    // `key` changes on every navigation, including one back to the same path.
  }, [location.key, state.returnTo]);

  if (page !== undefined) {
    if (group === 'subscription' && isSubscriptionSubPage(page)) {
      return <SubscriptionSubFrame frame={frame} page={page} />;
    }
    if (group !== 'account' || !isAccountSubPage(page)) return <Navigate to="/settings" replace />;
    return <AccountSubFrame frame={frame} page={page} />;
  }

  const support = group === SUPPORT;
  const current = GROUPS.find((entry) => entry.slug === group);
  if (group !== undefined && current === undefined && !support) {
    return <Navigate to="/settings" replace />;
  }

  return (
    <GroupFrame
      frame={frame}
      current={current ?? null}
      support={support}
      flash={state.flash ?? null}
    />
  );
}

/* ---------------------------------------------------------------- frames -- */

function Frame({
  frame,
  width,
  back,
  children,
}: {
  readonly frame: React.RefObject<HTMLDivElement | null>;
  readonly width: 'split' | 'menu';
  readonly back: ReactNode;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <div ref={frame} className="relative isolate grid min-h-full">
      <QuietGround className="-z-10" />
      <Screen width={width} className="w-full">
        <header className="flex">{back}</header>
        {children}
      </Screen>
    </div>
  );
}

/** The chip in the top corner: where Back goes, with an arrow that says so. */
function BackChip({
  to,
  returnTo,
  carry,
  className,
  children,
}: {
  readonly to: string;
  readonly returnTo?: string | undefined;
  /** More state the page it leads to needs, such as the offer's way back out. */
  readonly carry?: Record<string, unknown> | undefined;
  readonly className?: string | undefined;
  readonly children: ReactNode;
}): React.JSX.Element {
  const state = returnTo === undefined ? carry : { ...carry, returnTo };
  return (
    <ButtonLink size="sm" to={to} className={className} {...(state === undefined ? {} : { state })}>
      <ArrowLeftIcon size={18} aria-hidden="true" focusable="false" />
      {children}
    </ButtonLink>
  );
}

/** The suit line over the page's name, centered, as every settings page opens. */
function Title({
  className,
  size,
  lead,
  children,
}: {
  readonly className?: string | undefined;
  readonly size: 'page' | 'question';
  /** One quiet line under the title: the offer's. */
  readonly lead?: string | undefined;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <div className={cx('flex flex-col items-center gap-3 text-center', className)}>
      <SuitLine size="sm" />
      <h1
        tabIndex={-1}
        className={
          // A focus target, not a control: it takes focus on arrival so a
          // reader starts here, and draws no ring, as `ClassicGameOver`'s does.
          size === 'page'
            ? 'text-5xl leading-tight focus:outline-none tablet:text-6xl'
            : 'text-4xl leading-tight focus:outline-none'
        }
      >
        {children}
      </h1>
      {lead !== undefined && <p className="text-muted">{lead}</p>}
    </div>
  );
}

/**
 * The group doors. A labelled list item each (`settings.md`), the open one
 * shaded with `aria-current`, the way every current `MenuTile` is.
 */
function Doors({ current }: { readonly current: Group | null }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <ul className="m-0 flex list-none flex-col gap-4 p-0">
      {GROUPS.map(({ slug, key, suit }) => (
        <li key={slug}>
          <MenuTile
            to={`/settings/${slug}`}
            suit={suit}
            label={t(`settings.groups.${key}.title`)}
            hint={t(`settings.groups.${key}.hint`)}
            data-focus-key={`group-${slug}`}
            {...(current === slug ? { 'aria-current': 'page' as const } : {})}
          />
        </li>
      ))}
    </ul>
  );
}

/** Help and support, under the doors in both navigations. */
function SupportChip(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="mt-6 flex justify-center">
      <ButtonLink variant="ghost" to={`/settings/${SUPPORT}`} data-focus-key={SUPPORT}>
        {t('settings.support.title')}
      </ButtonLink>
    </div>
  );
}

function GroupFrame({
  frame,
  current,
  support,
  flash,
}: {
  readonly frame: React.RefObject<HTMLDivElement | null>;
  readonly current: GroupDoor | null;
  /** The support page, which stands where a group would with no door shaded. */
  readonly support: boolean;
  readonly flash: AccountFlash | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // `/settings` on a wide screen is the Game group; on a phone it is the list.
  const shown = current ?? GAME;
  const index = current === null && !support;
  const returnTo = support ? SUPPORT : `group-${shown.slug}`;

  return (
    <Frame
      frame={frame}
      width="split"
      back={
        index ? (
          <BackChip to="/">{t('settings.back')}</BackChip>
        ) : (
          <>
            <BackChip to="/settings" returnTo={returnTo} className="tablet:hidden">
              {t('settings.back')}
            </BackChip>
            <BackChip to="/" className="max-tablet:hidden">
              {t('settings.back')}
            </BackChip>
          </>
        )
      }
    >
      {index && (
        <Title size="page" className="tablet:hidden">
          {t('nav.settings')}
        </Title>
      )}
      <Title size="page" className={index ? 'max-tablet:hidden' : undefined}>
        {support ? t('settings.support.title') : t(`settings.groups.${shown.key}.title`)}
      </Title>
      {index && (
        <nav aria-label={t('nav.settings')} className="tablet:hidden">
          <Doors current={null} />
          <SupportChip />
        </nav>
      )}
      <div className={cx('flex items-start gap-8', index && 'max-tablet:hidden')}>
        <nav aria-label={t('nav.settings')} className="w-70 shrink-0 max-tablet:hidden">
          <Doors current={support ? null : shown.slug} />
          <SupportChip />
        </nav>
        {/* Keyed by path so an open Account row closes when the group changes. */}
        <div key={pathname} className="flex min-w-0 flex-1 flex-col gap-8">
          {support ? (
            <SupportPage />
          ) : (
            <>
              {shown.slug === 'game' && <GameSettings />}
              {shown.slug === 'look-sound' && <LookSoundSettings />}
              {shown.slug === 'account' && <AccountPage flash={flash} />}
              {shown.slug === 'subscription' && <SubscriptionPage />}
            </>
          )}
        </div>
      </div>
    </Frame>
  );
}

function AccountSubFrame({
  frame,
  page,
}: {
  readonly frame: React.RefObject<HTMLDivElement | null>;
  readonly page: AccountSubPage;
}): React.JSX.Element {
  const { t } = useTranslation();
  // Profile opens the delete page too, and Back from it goes back there. The
  // hook runs on every page: this frame stays mounted from one page to the next.
  const location = useLocation();
  const fromProfile = page === 'delete' && openedFromProfile(location.state);
  // Sign in or Create an account opened from a page outside Account, such as
  // the offer: Back goes to that page, as a finished sign-in does.
  const resume = page === 'sign-in' || page === 'register' ? resumeOf(location.state) : null;
  // A guest's delete page is for their guest data, so it is titled that way.
  const anonymous = useAnonymous();
  return (
    <Frame
      frame={frame}
      width="menu"
      back={
        resume === null ? (
          <BackChip to={fromProfile ? PROFILE_PATH : ACCOUNT_PATH} returnTo={page}>
            {fromProfile ? t('profile.title') : t('settings.groups.account.title')}
          </BackChip>
        ) : (
          <BackChip to={resume.to} carry={resume.state}>
            {t('settings.back')}
          </BackChip>
        )
      }
    >
      <Title size="question">
        {t(page === 'delete' && anonymous ? 'settings.account.deleteGuest' : SUBPAGE_TITLE[page])}
      </Title>
      <div className="flex flex-col gap-6">
        {page === 'register' && <RegisterPage />}
        {page === 'sign-in' && <SignInPage />}
        {page === 'forgot-password' && <ForgotPasswordPage />}
        {page === 'new-password' && <NewPasswordPage />}
        {page === 'need-name' && <NeedAccountPage wanted="name" />}
        {page === 'need-avatar' && <NeedAccountPage wanted="avatar" />}
        {page === 'avatar' && <AvatarPage />}
        {page === 'delete' && <DeleteAccountPage />}
      </div>
    </Frame>
  );
}

/**
 * The Subscription group's own pages: the offer, the plans, Subscribed,
 * restore and Change plan. Solo pages in the narrow column at every width,
 * because each is one decision (the `lobby-offer` sketch: "this is a
 * decision, not a settings page").
 *
 * Back goes where the page was opened from, which the pages carry between
 * them in router state (`subscriptionState`). The offer opened from outside
 * Settings, such as the out-of-credits sheet, returns to that path.
 *
 * Subscribed and Change plan belong to a member: once `/me` has answered, a
 * player it does not show as one goes back to Subscription. Nothing shows a
 * plan as owned that `/me` does not.
 */
function SubscriptionSubFrame({
  frame,
  page,
}: {
  readonly frame: React.RefObject<HTMLDivElement | null>;
  readonly page: SubscriptionSubPage;
}): React.JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const state = subscriptionState(location.state);
  const ready = useAuthStore((auth) => auth.ready);
  const standing = useAuthStore((auth) => standingOf(auth.user));
  // Not the plans: a purchase `/me` confirms makes a member there, on the way to Subscribed.
  const fits =
    page === 'subscribed'
      ? isMember(standing)
      : page === 'change-plan'
        ? standing === 'renewing'
        : true;
  if (ready && !fits) return <Navigate to={SUBSCRIPTION_PATH} replace />;

  const offer = subscriptionPath('offer');
  const back: {
    readonly to: string;
    readonly returnTo?: string | undefined;
    readonly carry?: Record<string, unknown> | undefined;
  } =
    page === 'offer'
      ? state.backTo === undefined
        ? { to: SUBSCRIPTION_PATH, returnTo: FOCUS.offer }
        : wayOut(state)
      : page === 'plans'
        ? { to: offer, returnTo: FOCUS.subscribe, carry: onward(state) }
        : page === 'restore'
          ? state.restoreFrom === 'offer'
            ? { to: offer, returnTo: FOCUS.restore, carry: onward(state) }
            : { to: SUBSCRIPTION_PATH, returnTo: FOCUS.restore }
          : page === 'change-plan'
            ? { to: SUBSCRIPTION_PATH, returnTo: FOCUS.manage }
            : wayOut(state);

  return (
    <Frame
      frame={frame}
      width="menu"
      back={
        <BackChip to={back.to} returnTo={back.returnTo} carry={back.carry}>
          {t('settings.back')}
        </BackChip>
      }
    >
      <Title
        size="page"
        lead={page === 'offer' ? t('subscription.offer.intro', { app: t('app.title') }) : undefined}
      >
        {t(SUBSCRIPTION_TITLE[page])}
      </Title>
      <div className="flex flex-col gap-8">
        {page === 'offer' && <OfferPage state={state} />}
        {page === 'plans' && <PlansPage state={state} />}
        {page === 'subscribed' && <SubscribedPage state={state} />}
        {page === 'restore' && <RestorePage state={state} />}
        {page === 'change-plan' && <ChangePlanPage />}
      </div>
    </Frame>
  );
}
